import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle, ChevronDown, Plus, X, Users, RefreshCw, Lock, Clock, Bot } from "lucide-react";
import axios from "axios";

const BOT_API = `${process.env.REACT_APP_BACKEND_URL}/api/settings/whatsapp-bot`;
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { PeriodToggle, BankTransferDialog } from "./BillingBank";

const BuyBox = ({ cfg, onDone, renew }) => {
  const [opt, setOpt] = useState(null);
  const [per, setPer] = useState(null);
  const [pay, setPay] = useState(false);
  useEffect(() => { api.billingOptions().then((o) => { setOpt(o); setPer(o.periods.find((p) => o.wa_addon?.prices?.[p.months] > 0) || o.periods[0]); }).catch(() => {}); }, []);
  const total = opt?.wa_addon?.prices?.[per?.months];
  if (!opt?.wa_addon?.enabled || !per) return null;
  if (cfg.pending_payment) return <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="org-wa-payment-pending"><Clock className="h-4 w-4" /> تحويلكم قيد المراجعة — تُفعّل الخدمة فور اعتماد الإيصال</div>;
  return (
    <div className="space-y-2 rounded-xl border border-emerald-300 bg-white p-3" data-testid="org-wa-buy">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="text-sm font-bold text-emerald-900">{renew ? "تجديد خدمة واتساب" : "اشترك في خدمة واتساب"}</div>
          <div className="text-xs text-slate-500">تنبيهات المستندات والغياب والعهد + رسائل الموظفين على واتساب</div></div>
        <div className="text-left"><span className="text-2xl font-black text-emerald-700" data-testid="org-wa-buy-price">{total}</span> <span className="text-xs text-slate-500">ر.س / {per.label}</span></div>
      </div>
      <PeriodToggle periods={opt.periods.filter((p) => opt.wa_addon.prices[p.months] > 0).map((p) => ({ ...p, discount: 0 }))} value={per} onChange={setPer} />
      <Button type="button" disabled={!total} onClick={() => setPay(true)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="org-wa-buy-btn">ادفع بالتحويل البنكي</Button>
      {pay && <BankTransferDialog plan={{ ...opt.wa_addon, price: total / per.months }} period={{ ...per, discount: 0 }} bank={opt.bank} onClose={() => setPay(false)} onDone={() => { setPay(false); onDone(); }} />}
    </div>
  );
};

const ERR = (e) => e?.response?.data?.detail || "تعذّر تنفيذ العملية";

const Recipients = ({ list, setList, groups }) => {
  const [v, setV] = useState("");
  const add = (x) => { const t = (x || "").trim(); if (t && !list.includes(t)) setList([...list, t]); setV(""); };
  const gname = (id) => groups.find((g) => g.id === id)?.name;
  return (
    <div className="space-y-1.5">
      <Label>المستلمون (أرقام المدير والموارد البشرية أو قروب واتساب)</Label>
      <div className="flex gap-2">
        <Input dir="ltr" value={v} onChange={(e) => setV(e.target.value)} placeholder="05xxxxxxxx أو 1203…@g.us" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(v))} data-testid="org-wa-recipient-input" />
        <Button type="button" variant="outline" onClick={() => add(v)} data-testid="org-wa-recipient-add"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="org-wa-recipients">
        {list.map((r, i) => (
          <span key={r} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs" data-testid={`org-wa-recipient-${i}`}>
            {r.includes("@g.us") ? <><Users className="h-3 w-3 text-emerald-600" /> {gname(r) || "قروب"}</> : <span dir="ltr">{r}</span>}
            <button type="button" onClick={() => setList(list.filter((x) => x !== r))} className="text-slate-400 hover:text-rose-600" data-testid={`org-wa-recipient-remove-${i}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {!list.length && <span className="text-xs text-slate-400">لا يوجد مستلمون بعد</span>}
      </div>
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1" data-testid="org-wa-groups">
          {groups.filter((g) => !list.includes(g.id)).map((g) => (
            <button key={g.id} type="button" onClick={() => add(g.id)} className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs text-emerald-800 hover:bg-emerald-200">+ {g.name}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const RequestBox = ({ cfg, onDone }) => {
  const [f, setF] = useState({ contact_name: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const pending = cfg.request && ["new", "in_progress"].includes(cfg.request.status);
  const send = async () => {
    setBusy(true);
    try { await api.requestOrgWa(f); toast.success("تم إرسال طلب التفعيل — سيتواصل معكم فريق المنصة"); onDone(); } catch (e) { toast.error(ERR(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="org-wa-locked">
      <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        <div>{cfg.expired ? <>انتهى اشتراك خدمة واتساب بتاريخ <b dir="ltr">{cfg.expires_at}</b>. اطلب التجديد لاستئناف الرسائل.</>
          : <>خدمة إرسال التنبيهات عبر واتساب غير مفعّلة لشركتكم. التنبيهات تصلكم حالياً على تيليجرام فقط.</>}</div>
      </div>
      <BuyBox cfg={cfg} onDone={onDone} renew={cfg.expired} />
      <div className="text-center text-[11px] text-slate-400">أو اطلب التفعيل وسيتواصل معكم فريق المنصة</div>
      {pending ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="org-wa-request-pending">
          <Clock className="h-4 w-4" /> تم استلام طلبكم ({cfg.request.status === "new" ? "جديد" : "قيد التنفيذ"}) — سيتواصل معكم فريق المنصة
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="اسم المسؤول" data-testid="org-wa-req-name" />
          <Input dir="ltr" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="05xxxxxxxx" data-testid="org-wa-req-phone" />
          <Textarea className="sm:col-span-2" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="ملاحظة (اختياري)" data-testid="org-wa-req-note" />
          <Button type="button" onClick={send} disabled={busy || !f.contact_name || !f.phone} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 sm:col-span-2" data-testid="org-wa-req-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {cfg.expired ? "اطلب التجديد" : "اطلب التفعيل"}
          </Button>
        </div>
      )}
    </div>
  );
};

const WaBotSwitch = () => {
  const [on, setOn] = useState(null);
  const [pre, setPre] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${BOT_API}`).then((r) => { setOn(r.data.enabled); setPre(r.data.prefix_only); }).catch(() => setOn(false)); }, []);
  const togglePre = async (v) => {
    try { await axios.post(`${BOT_API}/prefix`, { enabled: v }); setPre(v); toast.success(v ? "براء يرد فقط على الرسائل اللي تبدأ بكلمة «براء»" : "براء يرد على كل رسائل المسجّلين"); }
    catch (e) { toast.error(ERR(e)); }
  };
  const toggle = async (v) => {
    setBusy(true);
    try { await axios.post(BOT_API, { enabled: v }); setOn(v); toast.success(v ? "تم تفعيل بوت براء على رقم واتساب الشركة" : "تم إيقاف البوت"); }
    catch (e) { toast.error(ERR(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
    <label className="flex items-center justify-between gap-3 rounded-lg border border-emerald-300 bg-emerald-50/60 p-2.5" data-testid="org-wa-bot">
      <span className="text-sm"><b className="flex items-center gap-1.5"><Bot className="h-4 w-4 text-emerald-600" /> بوت براء على واتساب</b>
        <span className="block text-xs text-slate-500">المدير والمشرفين يرسلون لرقم الشركة من جوالاتهم المسجّلة ويجاوبهم براء (الطلبات، الملخص، المستندات، أداء المناديب…). اكتب 0 للقائمة. وطلبات الإجازة والسلف الجديدة توصلهم ويعتمدونها برد «موافق» أو «رفض». الأرقام غير المسجّلة لا يرد عليها أبداً.</span></span>
      {busy || on === null ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : <Switch checked={on} onCheckedChange={toggle} data-testid="org-wa-bot-switch" />}
    </label>
    {on && (
      <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-2.5" data-testid="org-wa-bot-prefix">
        <span className="text-sm"><b>يرد فقط إذا بدأت الرسالة بكلمة «براء»</b>
          <span className="block text-xs text-slate-500">مناسب إذا رقم الشركة رقمك الشخصي: محادثاتك العادية ما يتدخل فيها. مثال: «براء كم الطلبات أمس؟» أو «براء» للقائمة.</span></span>
        <Switch checked={pre} onCheckedChange={togglePre} data-testid="org-wa-bot-prefix-switch" />
      </label>
    )}
    </div>
  );
};

const QrBox = ({ onLinked }) => {
  const [qr, setQr] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.orgWaQr().then((d) => { setQr(d.qr); setErr(""); }).catch((e) => setErr(ERR(e))), []);
  useEffect(() => {
    load();
    const a = setInterval(load, 15000);
    const b = setInterval(onLinked, 5000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [load, onLinked]);
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-center sm:flex-row sm:text-right" data-testid="org-wa-qr-box">
      {qr ? <img src={qr} alt="QR" className="h-44 w-44 rounded-lg border bg-white p-1" data-testid="org-wa-qr-img" /> : <div className="flex h-44 w-44 items-center justify-center rounded-lg border bg-white">{err ? <span className="p-2 text-xs text-amber-700">{err}</span> : <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />}</div>}
      <div className="space-y-1 text-sm text-slate-700">
        <b className="block text-emerald-900">اربط رقم واتساب شركتكم</b>
        <p>1. افتح واتساب على جوال الشركة</p>
        <p>2. الإعدادات ← الأجهزة المرتبطة ← ربط جهاز</p>
        <p>3. امسح الباركود — يتحدّث تلقائياً</p>
        <Button type="button" variant="ghost" size="sm" onClick={load} className="gap-1.5 text-emerald-700" data-testid="org-wa-qr-refresh"><RefreshCw className="h-4 w-4" /> باركود جديد</Button>
      </div>
    </div>
  );
};

const ActiveBox = ({ cfg, apply }) => {
  const [f, setF] = useState({ recipients: cfg.recipients, employee_alerts: cfg.employee_alerts });
  const [status, setStatus] = useState(null);
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState("");
  const checkStatus = useCallback(() => api.orgWaStatus().then(setStatus).catch(() => {}), []);
  useEffect(() => { checkStatus(); }, [checkStatus]);
  const run = async (k, fn, ok) => { setBusy(k); try { const r = await fn(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); } catch (e) { toast.error(ERR(e)); } finally { setBusy(""); } };
  const st = status && (status.status === "authenticated" ? ["متصل", "bg-emerald-100 text-emerald-700"] : [status.detail || status.status, "bg-amber-100 text-amber-700"]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="org-wa-status">
        حالة الرقم: {st ? <span className={`rounded-full px-2 py-0.5 font-semibold ${st[1]}`}>{st[0]}</span> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        <button type="button" onClick={checkStatus} className="text-slate-400 hover:text-emerald-700" data-testid="org-wa-status-refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
        {cfg.expires_at && <span className="text-slate-500">· الاشتراك حتى <b dir="ltr">{cfg.expires_at}</b></span>}
        <span className="text-slate-500" data-testid="org-wa-usage">· رسائل هذا الشهر: <b>{cfg.usage?.sent || 0}</b>{cfg.usage?.failed ? <span className="text-rose-600"> (فشل {cfg.usage.failed})</span> : null}</span>
        {cfg.source !== "platform" && status?.status === "authenticated" && <button type="button" disabled={!!busy} className="ms-auto font-semibold text-rose-600 hover:underline" data-testid="org-wa-change-number"
          onClick={() => window.confirm("تغيير رقم واتساب الشركة؟ سيُفصل الرقم الحالي وتتوقف الرسائل وبراء لين تمسح الباركود بالجوال الجديد.") && run("lo", async () => { await api.orgWaLogout(); await checkStatus(); }, "تم فصل الرقم — امسح الباركود بالجوال الجديد")}>
          {busy === "lo" ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : "تغيير الرقم"}</button>}
      </div>
      {cfg.source !== "platform" && status && status.status !== "authenticated" && <QrBox onLinked={checkStatus} />}
      <Recipients list={f.recipients} setList={(recipients) => setF({ ...f, recipients })} groups={groups} />
      <Button type="button" variant="ghost" size="sm" disabled={!!busy} onClick={() => run("g", async () => { const d = await api.orgWaGroups(); setGroups(d.groups); if (!d.groups.length) toast.info("لا توجد قروبات في الرقم المربوط"); })} className="gap-1.5 text-emerald-700" data-testid="org-wa-load-groups">
        {busy === "g" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} جلب قروبات واتساب
      </Button>
      <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-2.5">
        <span className="text-sm"><b>رسائل شخصية للموظفين</b><span className="block text-xs text-slate-500">{f.employee_alerts ? "مفعّل: كل موظف يستلم إشعاراته (انتهاء وثيقته، الطلبات، الإنذارات) على جواله المسجّل" : "متوقف: لا يُرسل أي شيء للموظفين — التنبيهات تصل للمالك والأرقام المضافة أعلاه فقط"}</span></span>
        {busy === "e" ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : <Switch checked={f.employee_alerts} onCheckedChange={(v) => run("e", async () => { apply(await api.saveOrgWa({ ...f, employee_alerts: v })); setF((x) => ({ ...x, employee_alerts: v })); }, v ? "تم تفعيل رسائل الموظفين" : "تم إيقاف رسائل الموظفين — التنبيهات للمالك فقط")} data-testid="org-wa-employee-switch" />}
      </label>
      {f.employee_alerts && cfg.employees_no_phone > 0 && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="org-wa-no-phone-warn">
        ⚠️ {cfg.employees_no_phone} من {cfg.employees_total} موظف بدون رقم جوال — لن تصلهم أي رسالة. أضف رقم الجوال في ملف كل موظف.</p>}
      <WaBotSwitch />
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => run("s", async () => apply(await api.saveOrgWa(f)), "تم الحفظ")} disabled={!!busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="org-wa-save">{busy === "s" && <Loader2 className="h-4 w-4 animate-spin" />} حفظ</Button>
        <Button type="button" variant="outline" onClick={() => run("t", api.testOrgWa, (r) => `أُرسلت الرسالة إلى ${r.count} من ${r.total}`)} disabled={!!busy} className="gap-1.5 border-emerald-500 text-emerald-700" data-testid="org-wa-test">{busy === "t" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} رسالة تجريبية</Button>
      </div>
    </div>
  );
};

export const OrgWhatsappAlerts = ({ defaultOpen = false }) => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const load = useCallback(() => api.getOrgWa().then(setCfg).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50/50 sm:col-span-2" data-testid="org-whatsapp-alerts">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-right hover:bg-emerald-100/50" data-testid="org-wa-toggle">
        <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-emerald-900">
          <MessageCircle className="h-5 w-5 text-emerald-600" /> تنبيهات واتساب
          <span className={`rounded-full px-2 py-0.5 text-[10px] ${cfg.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid="org-wa-badge">{cfg.active ? "مُفعّل" : cfg.expired ? "منتهي" : "غير مُفعّل"}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-emerald-600 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-emerald-100 bg-white/70 p-3">{cfg.active ? <>
        <ActiveBox cfg={cfg} apply={(d) => setCfg({ ...cfg, ...d })} />
        {cfg.expires_at && <div className="mt-3"><BuyBox cfg={cfg} onDone={load} renew /></div>}
      </> : <RequestBox cfg={cfg} onDone={load} />}</div>}
    </div>
  );
};
